import React, { useCallback, useEffect, useMemo, useState } from "react";
import TrinketPair from "./TrinketPair";
import Trinket from "./Trinket";
import classNames from "classnames";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as QuestionMark } from "svg/question-line.svg";
import MediaQuery from "components/MediaQuery";
import useSorting from "hooks/useSorting";
import _flatten from "lodash.flatten";

const TrinketsContainer = (props) => {
  const { isHealer, isTank, isMobile, trinkets = [], trinketPairs, totalTrinkets, encounterType } = props;

  const [selectedTrinketIndex, setSelectedTrinketIndex] = useState(0);
  const [selectedTrinket, setSelectedTrinket] = useState(null);

  // Change to false when eventually using view all button
  const [isViewAll, setIsViewAll] = useState(true);
  const [filteredTrinkets, setFilteredTrinkets] = useState(trinkets);

  useEffect(() => {
    const filtered = isViewAll ? trinkets : trinkets.filter((el) => el.count / totalTrinkets >= 0.01);

    const sorted = filtered.sort((a, b) => b.count - a.count);
    setFilteredTrinkets(sorted);
  }, [isViewAll, trinkets]);

  useEffect(() => {
    // Reset trinket selection to first trinket whenever filters change
    if (trinkets) {
      setSelectedTrinket(trinkets[0]);
      setSelectedTrinketIndex(0);
    }
  }, [trinkets]);

  const topPairs = useMemo(() => {
    if (!trinketPairs) {
      return [];
    }

    const total = trinketPairs.reduce((acc, x) => acc + x.reduce((xAcc, y) => xAcc + y.count, 0), 0);
    const pairs = trinketPairs.map((x, i) =>
      x.map((y) => {
        const percent = ((y.count / total) * 100).toFixed(1);
        return {
          ...y,
          firstTrinket: trinkets[i],
          perc: percent,
        };
      }),
    );
    const res = _flatten(pairs).sort((x, y) => y.count - x.count);
    return res.slice(0, 8);
  }, [trinketPairs, trinkets]);

  const pairsToShow = useMemo(() => {
    if (!topPairs && selectedTrinketIndex < 0) {
      return [];
    }
    if (selectedTrinketIndex < 0) {
      return topPairs;
    }

    return trinketPairs[selectedTrinketIndex];
  }, [selectedTrinketIndex, trinketPairs, topPairs]);

  return (
    <div className="trinkets-container">
      {/* Use media queries to prevent title changing on render */}
      <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE" renderNullOnFail>
        <TrinketTable
          title="Best Trinket Combos"
          trinkets={filteredTrinkets}
          selectedIndex={selectedTrinketIndex}
          setSelectedIndex={setSelectedTrinketIndex}
          selectedTrinket={selectedTrinket}
          setSelectedTrinket={setSelectedTrinket}
          trinketPairs={trinketPairs}
          encounterType={encounterType}
          isHealer={isHealer}
          isTank={isTank}
        />
      </MediaQuery>
      <MediaQuery min="TABLET" max="DESKTOP_LARGE" renderNullOnFail>
        <TrinketTable
          title="Trinkets"
          trinkets={filteredTrinkets}
          selectedIndex={selectedTrinketIndex}
          setSelectedIndex={setSelectedTrinketIndex}
          setSelectedTrinket={setSelectedTrinket}
          selectedTrinket={selectedTrinket}
          trinketPairs={trinketPairs}
          encounterType={encounterType}
          isHealer={isHealer}
          isTank={isTank}
        />
      </MediaQuery>
      <MediaQuery min="TABLET" max="DESKTOP_LARGE" renderNullOnFail>
        <TrinketTable
          title="Best Trinket Combos"
          trinkets={pairsToShow}
          selectedTrinket={selectedTrinket}
          isMobile={isMobile}
          isHealer={isHealer}
          isTank={isTank}
          encounterType={encounterType}
          pairMode
        />
      </MediaQuery>
    </div>
  );
};

const TrinketTable = (props) => {
  const {
    title,
    trinkets = [],
    selectedIndex,
    setSelectedIndex,
    trinketPairs,
    isHealer,
    isTank,
    selectedTrinket,
    setSelectedTrinket,
    limit,
    className,
    noHeaders = false,
    encounterType = "raid",
    pairMode = false,
  } = props;

  let raidHeader = {
    title: "DPS",
    tooltip:
      "DPS is the aggregated Damage Per Second score that each DPS spec does when using this trinket or trinket combo in the top rankings of Raid and Mythic+ Dungeons.",
    key: "dps",
  };
  if (isHealer) {
    raidHeader = {
      title: "HPS",
      tooltip:
        "HPS is the aggregated Healing Per Second score that each Healer spec does when using this trinket in the top rankings of Raid and Mythic+ Dungeons.",
      key: "hps",
    };
  } else if (isTank) {
    raidHeader = {
      title: "Tank DPS",
      tooltip:
        "For Tanks we opted to show their aggregated DPS score when using this trinket or trinket combo as a temporary placeholder metric while we search for a more valuable metric that tank players care about.",
      key: "dps",
    };
  }
  const headers = [
    encounterType === "raid"
      ? { ...raidHeader }
      : {
          title: "Max Key",
          tooltip: "The highest key that this particular trinket was used in and completed on time.",
          key: "max_key",
        },
    {
      title: "Popularity",
      tooltip: "Popularity is how often these trinkets were used by players in the top rankings of Raid or Mythic+ Dungeons",
      key: "count",
    },
  ];

  const handleClick = useCallback(
    (i, trinket) => {
      if (pairMode || selectedIndex === i) {
        return;
      }

      setSelectedIndex(i);
      setSelectedTrinket(trinket);
    },
    [setSelectedIndex, setSelectedTrinket, selectedIndex],
  );

  const data = useMemo(() => {
    return trinkets.map((x, i) => {
      const pairs = !pairMode && trinketPairs?.length > i ? trinketPairs[i] : [];
      return {
        ...x,
        pairs: pairs,
        index: i,
      };
    });
  }, [trinkets, trinketPairs, headers]);

  const {
    sorted: sortedData,
    setSort,
    sortClass,
    sortMetric,
  } = useSorting({
    data,
    fieldNames: headers,
    defaultSort: "Popularity",
  });

  return (
    <div
      className={classNames("trinket-table", className, {
        pairs: pairMode,
        single: !pairMode,
      })}
    >
      {!noHeaders && (
        <div className="grid header">
          <div className="table-title">{title}</div>
          {headers.map((x, i) => {
            return (
              <div
                key={i}
                className={classNames("col-title sortable", {
                  [sortClass]: sortMetric === x.title,
                })}
                onClick={() => setSort(x.title)}
              >
                <span>{x.title}</span>
                <TooltipContainer tooltipInfo={x.tooltip}>
                  <div className="tooltip-icon">
                    <QuestionMark />
                  </div>
                </TooltipContainer>
              </div>
            );
          })}
        </div>
      )}
      <div className="table-content">
        {sortedData.slice(0, limit).map((x, i) => {
          return pairMode ? (
            <TrinketPair
              key={i}
              trinket={x}
              selectedTrinket={selectedTrinket || x.firstTrinket}
              isTank={isTank}
              isHealer={isHealer}
              encounterType={encounterType}
              nested={noHeaders}
            />
          ) : (
            <Trinket
              key={i}
              trinket={x}
              isHealer={isHealer}
              isTank={isTank}
              trinketPairs={x.pairs}
              isSelected={selectedTrinket?.item_id === x?.item_id}
              encounterType={encounterType}
              onClick={(trinket) => handleClick(x.index, trinket)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TrinketsContainer;
export { TrinketTable };
